import { useState, useRef, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { formatFileSize } from '../utils/formatters';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { SnackbarType } from '../components/Snackbar';
import { convertBmpToPng, isBmpFile } from '../utils/imageFormat';

export interface ImageProcessorOptions {
  maxSizeMB?: number;
  useWebWorker?: boolean;
  maxWidthOrHeight?: number;
  initialZoom?: number;
  initialRotation?: number;
}

export interface Position {
  x: number;
  y: number;
}

interface ImageProcessorState {
  file: File | null;
  originalFile: File | null;
  imageSize: number | null;
  compressedSize: number | null;
  zoom: number;
  rotation: number;
  position: Position;
  isEditorOpen: boolean;
}

const defaultOptions: ImageProcessorOptions = {
  maxSizeMB: 1,
  useWebWorker: true,
  maxWidthOrHeight: 1920,
  initialZoom: 1,
  initialRotation: 0,
};

export const useImageProcessor = (options: ImageProcessorOptions = {}) => {
  const mergedOptions = { ...defaultOptions, ...options };
  const showSnackbar = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<any>(null);
  // ファイル選択ごとに進める連番。BMP 変換の完了時に、より新しい選択やキャンセルがあれば結果を捨てる
  const selectionIdRef = useRef(0);

  const [state, setState] = useState<ImageProcessorState>({
    file: null,
    originalFile: null,
    imageSize: null,
    compressedSize: null,
    zoom: mergedOptions.initialZoom || 1,
    rotation: mergedOptions.initialRotation || 0,
    position: { x: 0.5, y: 0.5 },
    isEditorOpen: false,
  });

  // ファイル選択のハンドラー
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const selectedFile = e.target.files[0];
    const target = e.target;
    const selectionId = ++selectionIdRef.current;

    const openEditor = (file: File) => {
      setState(prev => ({
        ...prev,
        originalFile: file,
        imageSize: file.size,
        file,
        isEditorOpen: true,
      }));
    };

    // BMP はサーバーで受け付けないため PNG に変換してから編集に回す
    if (isBmpFile(selectedFile)) {
      convertBmpToPng(selectedFile)
        .then((converted) => {
          if (selectionId !== selectionIdRef.current) return;
          openEditor(converted);
        })
        .catch((error) => {
          if (selectionId !== selectionIdRef.current) return;
          console.error('BMP画像の変換に失敗:', error);
          showSnackbar('BMP画像の読み込みに失敗しました。', SnackbarType.ERROR);
          target.value = '';
        });
      return;
    }

    // ファイル形式の検証（PNG・JPG・BMPのみ許可）
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(selectedFile.type)) {
      showSnackbar('PNG・JPG・BMP形式のファイルのみ選択可能です。', SnackbarType.WARNING);
      // ファイル入力をリセット
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    openEditor(selectedFile);
  }, [showSnackbar]);

  // 画像の圧縮処理
  const compressImage = useCallback(async (blob: Blob): Promise<Blob> => {
    if (blob.size <= (mergedOptions.maxSizeMB || 1) * 1024 * 1024) {
      return blob;
    }

    try {
      const options = {
        maxSizeMB: mergedOptions.maxSizeMB || 1,
        maxWidthOrHeight: mergedOptions.maxWidthOrHeight || 1920,
        useWebWorker: mergedOptions.useWebWorker || true,
      };

      const compressedBlob = await imageCompression(blob, options);
      
      // 圧縮結果を通知
      if (compressedBlob.size < blob.size) {
        showSnackbar(
          `画像を ${formatFileSize(blob.size)} から ${formatFileSize(compressedBlob.size)} に圧縮しました`, 
          SnackbarType.SUCCESS
        );
      }
      
      return compressedBlob;
    } catch (error) {
      console.error('画像圧縮に失敗:', error);
      showSnackbar('画像圧縮に失敗しました', SnackbarType.ERROR);
      return blob;
    }
  }, [mergedOptions, showSnackbar]);

  // 編集した画像の保存
  const saveEditedImage = useCallback(async () => {
    if (!editorRef.current) return null;

    return new Promise<File | null>((resolve) => {
      let attempts = 0;
      const maxAttempts = 20; // 最大2秒待機（100ms × 20）
      
      // キャンバスを取得して処理する関数
      const processCanvas = () => {
        try {
          // 直接getImageScaledToCanvas()を呼び出してキャンバスを取得
          const canvas = editorRef.current.getImageScaledToCanvas();
          
          // キャンバスが有効か確認（幅と高さが0より大きいことを確認）
          if (!canvas || canvas.width === 0 || canvas.height === 0) {
            attempts++;
            if (attempts >= maxAttempts) {
              console.error('Canvas not ready after multiple attempts');
              resolve(null);
              return;
            }
            // キャンバスが準備できるまで待機
            setTimeout(processCanvas, 100);
            return;
          }
          
          // 画像が実際に描画されているか確認（ピクセルデータをチェック）
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.error('Failed to get canvas context');
            resolve(null);
            return;
          }
          
          // 元のファイル形式を取得（JPEGの場合はJPEG、それ以外はPNG）
          const originalType = state.originalFile?.type || 'image/png';
          const isJpeg = originalType === 'image/jpeg' || originalType === 'image/jpg';
          const mimeType = isJpeg ? 'image/jpeg' : 'image/png';
          const quality = isJpeg ? 0.92 : undefined; // JPEGの場合は品質を指定
          
          canvas.toBlob(async (blob: Blob | null) => {
            if (!blob) {
              console.error('Failed to create blob from canvas');
              resolve(null);
              return;
            }
            
            // 必要に応じて圧縮
            const finalBlob = await compressImage(blob);
            setState(prev => ({
              ...prev,
              compressedSize: finalBlob.size,
            }));
            
            // ファイルオブジェクトに変換（元の拡張子を保持）
            const originalName = state.originalFile?.name || 'edited-image.png';
            const extension = isJpeg ? '.jpg' : '.png';
            const fileName = originalName.replace(/\.[^.]+$/, extension);
            const file = new File([finalBlob], fileName, { type: mimeType });
            
            setState(prev => ({
              ...prev,
              file,
              isEditorOpen: false,
            }));
            
            resolve(file);
          }, mimeType, quality);
        } catch (error) {
          console.error('Error processing canvas:', error);
          resolve(null);
        }
      };

      // 少し待ってから処理を開始（AvatarEditorが完全にレンダリングされるのを待つ）
      setTimeout(processCanvas, 100);
    });
  }, [state.originalFile, compressImage]);

  // 編集のキャンセル
  const cancelEdit = useCallback(() => {
    selectionIdRef.current++;
    setState(prev => ({
      ...prev,
      file: null,
      originalFile: null,
      imageSize: null,
      compressedSize: null,
      isEditorOpen: false,
    }));
    
    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // 状態のリセット
  const resetState = useCallback(() => {
    selectionIdRef.current++;
    setState({
      file: null,
      originalFile: null,
      imageSize: null,
      compressedSize: null,
      zoom: mergedOptions.initialZoom || 1,
      rotation: mergedOptions.initialRotation || 0,
      position: { x: 0.5, y: 0.5 },
      isEditorOpen: false,
    });
    
    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [mergedOptions.initialZoom, mergedOptions.initialRotation]);

  // ズーム値の変更
  const handleZoomChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      zoom: parseFloat(e.target.value),
    }));
  }, []);

  // 回転値の変更
  const handleRotationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      rotation: parseInt(e.target.value, 10),
    }));
  }, []);

  // 位置の変更
  const handlePositionChange = useCallback((position: Position) => {
    setState(prev => ({
      ...prev,
      position,
    }));
  }, []);

  return {
    file: state.file,
    originalFile: state.originalFile,
    imageSize: state.imageSize,
    compressedSize: state.compressedSize,
    zoom: state.zoom,
    rotation: state.rotation,
    position: state.position,
    isEditorOpen: state.isEditorOpen,
    editorRef,
    fileInputRef,
    handleFileChange,
    saveEditedImage,
    cancelEdit,
    resetState,
    handleZoomChange,
    handleRotationChange,
    handlePositionChange,
  };
}; 
